import { zodResolver } from '@hookform/resolvers/zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  Label,
  FormMessage,
  Input,
} from 'me-component-library';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { useGetEmails } from '../../../hooks';
import { useTranslation } from '../../../../_shared/hooks';

interface EmailAddDialogAddressFormProps {
  address: string;
  raiseAddress: (address: string) => void;
}

export const EmailAddDialogAddressForm: React.FC<
  EmailAddDialogAddressFormProps
> = ({ raiseAddress }) => {
  const { data: emailData } = useGetEmails({ fromCache: true });
  const t = useTranslation();

  //TODO this needs a backend endpoint that will provide allowed domains, for now we are assuming that the first email is the allowed domain only
  const allowedDomain = emailData?.[0]?.email.split('@')[1];

  const formSchema = z.object({
    email: z
      .string()
      .email({
        message: t('dashboard.emailAddDialogs.errors.invalidEmail'),
      })
      .refine(
        value => {
          const newDomain = value.split('@')[1];
          if (!newDomain) {
            return false;
          }
          if (allowedDomain && newDomain.endsWith(allowedDomain)) {
            return true;
          }
          return false;
        },
        {
          message: t('dashboard.emailAddDialogs.errors.invalidDomain'),
        },
      )
      .refine(
        value => {
          if (!emailData?.some(e => e.email === value)) {
            return true;
          }
          return false;
        },
        {
          message: t('dashboard.emailAddDialogs.errors.emailAlreadyExists'),
        },
      ),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    mode: 'onBlur',
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
    },
  });

  const handleSubmit = async (values: z.infer<typeof formSchema>) => {
    raiseAddress(values.email);
  };

  return (
    <Form {...form}>
      <form onBlur={form.handleSubmit(handleSubmit)}>
        <Label className="select-none">
          {t('dashboard.emailAddDialogs.form.emailAddressLabel')}
        </Label>
        <div className="flex gap-4">
          <FormField
            control={form.control}
            name="email"
            key="email"
            render={({ field }) => (
              <FormItem className="flex grow flex-col">
                <FormControl>
                  <Input
                    placeholder={t(
                      'dashboard.emailAddDialogs.form.emailAddressLabel',
                    )}
                    data-testid="email-input"
                    className="w-full"
                    type="text"
                    {...field}
                  />
                </FormControl>
                {form.formState.errors.email && (
                  <FormMessage>
                    {form.formState.errors.email?.message}
                  </FormMessage>
                )}
              </FormItem>
            )}
          />
        </div>
      </form>
    </Form>
  );
};
